import React, { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, X } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { useCart } from "@/context/CartContext";
import StoreShell from "@/components/StoreShell";
import { hexA } from "@/lib/storeutils";
import { ProductCard, Splash, ErrorView } from "./Storefront";
import { PharmaCard } from "./PharmacyHome";
import { MediCard } from "./MediHome";
import { BookCard } from "./BookHome";
import { InkCard } from "./InkHome";
import { AromaCard } from "@/components/aroma/AromaParts";
import { MarketCard } from "@/components/market/MarketParts";
import { MeatCard } from "@/components/meat/MeatParts";
import { ArtisanCard } from "@/components/artisan/ArtisanParts";
import { VerdantCard } from "@/components/verdant/VerdantParts";

const SORTS = [["new", "Newest first"], ["price-asc", "Price: low to high"], ["price-desc", "Price: high to low"], ["name", "Name: A to Z"]];

/** The product card of the active theme — the Shop page reuses each theme's own card design. */
export function themeCard(layout, p, s, cart) {
  if (layout === "pharmacy") return <PharmaCard p={p} s={s} cart={cart} />;
  if (layout === "medi") return <MediCard p={p} s={s} cart={cart} />;
  if (layout === "books") return <BookCard p={p} s={s} cart={cart} />;
  if (layout === "bookish") return <InkCard p={p} s={s} cart={cart} />;
  if (layout === "verdant") return <VerdantCard p={p} s={s} cart={cart} />;
  if (layout === "aroma") return <AromaCard p={p} s={s} cart={cart} />;
  if (layout === "market") return <MarketCard p={p} s={s} cart={cart} />;
  if (layout === "meatverse") return <MeatCard p={p} s={s} cart={cart} />;
  if (layout === "artisan") return <ArtisanCard p={p} s={s} cart={cart} />;
  return <ProductCard p={p} s={s} cart={cart} />;
}

/** Standard title block used by Shop / About / Blogs / Contact. */
export function PageTitle({ s, title, subtitle }) {
  const { c, font, muted } = s;
  return (
    <div className="border-b" style={{ background: hexA(c.primary, 0.06), borderColor: s.border }}>
      <div className="max-w-6xl mx-auto px-5 py-9">
        <h1 className="text-3xl md:text-4xl font-extrabold" style={{ fontFamily: font }} data-testid="page-title">{title}</h1>
        {subtitle && <p className="mt-2 text-sm md:text-base max-w-2xl" style={{ color: muted }}>{subtitle}</p>}
      </div>
    </div>
  );
}

export default function Shop() {
  const s = useStore();
  const cart = useCart();
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const category = params.get("category") || "";
  const sort = params.get("sort") || "new";

  const set = (k, v) => setParams((prev) => { const next = new URLSearchParams(prev); if (v) next.set(k, v); else next.delete(k); return next; }, { replace: true });

  const list = useMemo(() => {
    const t = q.trim().toLowerCase();
    const out = s.products.filter((p) => (!category || p.category === category) && (!t || `${p.name} ${p.category || ""} ${p.brand || ""}`.toLowerCase().includes(t)));
    const by = { "price-asc": (a, b) => a.price - b.price, "price-desc": (a, b) => b.price - a.price, name: (a, b) => a.name.localeCompare(b.name) };
    if (by[sort]) out.sort(by[sort]);                    // "new" keeps the catalogue order (newest first from the API)
    return out;
  }, [s.products, q, category, sort]);

  if (s.loading) return <Splash />;
  if (s.error) return <ErrorView error={s.error} />;
  const { c, border, muted } = s;
  const layout = s.theme?.layout;
  const cols = layout === "medi" || layout === "books" || layout === "market" || layout === "meatverse" ? "lg:grid-cols-5" : "lg:grid-cols-4";
  const filtered = Boolean(q || category);

  return (
    <StoreShell>
      <PageTitle s={s} title={category || "Shop"} subtitle={category ? `Browsing ${category}` : `Everything available at ${s.store.name}.`} />
      <section className="max-w-6xl mx-auto px-5 py-8" id="shop" data-testid="shop-page">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 rounded-full px-4 py-2 border flex-1 min-w-[220px] max-w-md" style={{ borderColor: border, background: s.c.surface }}>
            <Search size={15} style={{ color: muted }} />
            <input value={q} onChange={(e) => set("q", e.target.value)} placeholder="Search products…" aria-label="Search products" data-testid="shop-search" className="bg-transparent outline-none text-sm w-full" style={{ color: c.text }} />
          </div>
          <select value={sort} onChange={(e) => set("sort", e.target.value === "new" ? "" : e.target.value)} aria-label="Sort products" data-testid="shop-sort"
            className="rounded-full border px-4 py-2 text-sm bg-transparent outline-none" style={{ borderColor: border, color: c.text }}>
            {SORTS.map(([v, l]) => <option key={v} value={v} style={{ color: "#111" }}>{l}</option>)}
          </select>
          {filtered && <button type="button" onClick={() => setParams({}, { replace: true })} className="inline-flex items-center gap-1 text-sm font-semibold" style={{ color: c.primary }}><X size={14} /> Clear</button>}
          <span className="ml-auto text-sm" style={{ color: muted }} data-testid="shop-count">{list.length} product{list.length === 1 ? "" : "s"}</span>
        </div>

        {s.categories.length > 0 && (
          <div className="flex gap-2 flex-wrap mt-5" data-testid="shop-categories">
            {["", ...s.categories].map((cat) => (
              <button key={cat || "all"} type="button" onClick={() => set("category", cat)} data-testid={`shop-cat-${cat || "all"}`} className="px-4 py-1.5 rounded-full text-sm font-medium transition-all"
                style={category === cat ? { background: c.primary, color: "#fff" } : { background: c.surface, color: c.text, border: `1px solid ${border}` }}>{cat || "All"}</button>
            ))}
          </div>
        )}

        {list.length === 0 ? (
          <div className="text-center py-20" style={{ color: muted }} data-testid="shop-empty">
            <p className="text-base font-semibold" style={{ color: c.text }}>No products found</p>
            <p className="text-sm mt-1">{filtered ? "Try a different search or category." : "This store has not published any products yet."}</p>
            {filtered && <button type="button" onClick={() => setParams({}, { replace: true })} className="mt-4 px-5 py-2 rounded-full text-sm font-bold" style={{ background: c.primary, color: "#fff" }}>Show all products</button>}
          </div>
        ) : (
          <div className={`grid grid-cols-2 md:grid-cols-3 ${cols} gap-5 mt-6`} data-testid="storefront-products">
            {list.map((p) => <div key={p.id}>{themeCard(layout, p, s, cart)}</div>)}
          </div>
        )}
        <div className="mt-10 text-center"><Link to={`/store/${s.slug}`} className="text-sm font-semibold underline" style={{ color: c.primary }}>← Back to home</Link></div>
      </section>
    </StoreShell>
  );
}
